import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import {
  LDSK_SIZE,
  DSK_SIZE,
  LTP_SIZE,
  TBL_SIZE,
  MBL_SIZE
} from "@directv-digital/dds.ui.dtv-theme";
import Head from "next/head";
import {
  IMediaModuleImageCardProps,
  MediaModuleImageVariant
} from "./media-module.types";
import { mediaModuleStyles } from "./media-module.style";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { EmailCaptureForm } from "./email-capture-form/email-capture-form";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import DtvAvatar from "@directv-digital/dds.ui.dtv-avatar";

// eslint-disable-next-line complexity
export const MediaModuleImageCard = (props: IMediaModuleImageCardProps) => {
  try {
    const {
      id,
      textColor,
      backgroundImage,
      backgroundColor,
      eyebrow,
      headline,
      description,
      cta,
      secondaryCta,
      tertiaryCta,
      legalText,
      image,
      aboveTheFold,
      variant,
      cardHeight,
      emailCapture,
      ctaMobileLayout,
      imageAlignment
    } = props;

    const isBackgroundImageExists =
      backgroundImage && Object.keys(backgroundImage).length > 0;
    const isImageExists = image && Object.keys(image).length > 0;

    const styles = mediaModuleStyles({
      backgroundColor,
      textColor,
      cardHeight,
      ctaMobileLayout,
      imageAlignment,
      hasTertiaryCta: !!tertiaryCta?.label
    });
    const [locationOrigin, setLocationOrigin] = React.useState<string>("");
    React.useEffect(() => {
      setLocationOrigin(window.location.origin);
    }, []);
    const handleGetTargeurl = (targeturl: string | undefined) => {
      if (!targeturl) {
        return undefined;
      }
      return !targeturl?.startsWith("http") && locationOrigin ?
        `${locationOrigin}${targeturl}` :
        targeturl;
    };

    return (
      <DtvBox
        sx={{
          ...styles.cardMainContainer,
          ...(!isBackgroundImageExists && styles.cardBackground),
          ...(variant === MediaModuleImageVariant.ONE_UP && {
            ...styles.cardMainContainer1Up,
            ...(!isBackgroundImageExists && styles.cardBackground1Up)
          })
        }}
      >
        {isBackgroundImageExists && (
          <DtvBox sx={styles.bgImageContainer}>
            {aboveTheFold && (
              <Head>
                {backgroundImage?.xl?.url && (
                  <link
                    as="image"
                    fetchPriority="high"
                    href={`${backgroundImage?.xl?.url}`}
                    media={LDSK_SIZE}
                    rel="preload"
                  />
                )}
                {backgroundImage?.lg?.url && (
                  <link
                    as="image"
                    fetchPriority="high"
                    href={`${backgroundImage?.lg?.url}`}
                    media={DSK_SIZE}
                    rel="preload"
                  />
                )}
                {backgroundImage?.md?.url && (
                  <link
                    as="image"
                    fetchPriority="high"
                    href={`${backgroundImage?.md?.url}`}
                    media={LTP_SIZE}
                    rel="preload"
                  />
                )}
                {backgroundImage?.sm?.url && (
                  <link
                    as="image"
                    fetchPriority="high"
                    href={`${backgroundImage?.sm?.url}`}
                    media={TBL_SIZE}
                    rel="preload"
                  />
                )}
                {backgroundImage?.xs?.url && (
                  <link
                    as="image"
                    fetchPriority="high"
                    href={`${backgroundImage?.xs?.url}`}
                    media={MBL_SIZE}
                    rel="preload"
                  />
                )}
              </Head>
            )}

            <picture>
              {backgroundImage?.xl?.url && (
                <source
                  media={LDSK_SIZE}
                  srcSet={`${backgroundImage?.xl?.url}`}
                />
              )}
              {backgroundImage?.lg?.url && (
                <source
                  media={DSK_SIZE}
                  srcSet={`${backgroundImage?.lg?.url}`}
                />
              )}
              {backgroundImage?.md?.url && (
                <source
                  media={LTP_SIZE}
                  srcSet={`${backgroundImage?.md?.url}`}
                />
              )}
              {backgroundImage?.sm?.url && (
                <source
                  media={TBL_SIZE}
                  srcSet={`${backgroundImage?.sm?.url}`}
                />
              )}
              {backgroundImage?.xs?.url && (
                <source
                  media={MBL_SIZE}
                  srcSet={`${backgroundImage?.xs?.url}`}
                />
              )}
              {/* Generated by Copilot- Start - 2026-04-10 17:28:57 */}
              <img
                alt={backgroundImage?.alt || backgroundImage?.xl?.alt || backgroundImage?.lg?.alt || backgroundImage?.md?.alt || backgroundImage?.sm?.alt || backgroundImage?.xs?.alt || ""}
                loading={aboveTheFold ? "eager" : "lazy"}
                src={`${backgroundImage?.xl?.url || backgroundImage?.lg?.url || backgroundImage?.md?.url || backgroundImage?.sm?.url || backgroundImage?.xs?.url}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
              />
              {/* Generated by Copilot- End - 2026-04-10 17:28:57 */}
            </picture>
          </DtvBox>
        )}
        <DtvBox
          sx={{
            ...styles.cardContainer,
            ...(variant === MediaModuleImageVariant.ONE_UP &&
              styles.cardContainer1Up)
          }}
        >
          <DtvBox
            sx={{
              ...styles.contentContainer,
              ...(variant === MediaModuleImageVariant.ONE_UP &&
                styles.contentContainer1Up)
            }}
          >
            {eyebrow?.content && (
              <DtvText
                variant={"eyebrowregular"}
                {...eyebrow}
                component={"div"}
                sx={{
                  ...styles.eyebrow,
                  ...(variant === MediaModuleImageVariant.ONE_UP &&
                    styles.eyebrow1Up)
                }}
              />
            )}
            {headline?.content && (
              <DtvText
                variant={"subhead1medium"}
                {...headline}
                sx={{
                  ...styles.headline,
                  ...(variant === MediaModuleImageVariant.ONE_UP &&
                    styles.headline1Up),
                  ...(eyebrow?.content && styles.headlineSpacing),
                  ...(eyebrow?.content &&
                    variant === MediaModuleImageVariant.ONE_UP &&
                    styles.headlineSpacing1Up)
                }}
              />
            )}
            {description?.content && (
              <DtvText
                variant={"body1"}
                {...description}
                sx={{
                  ...styles.description,
                  ...(variant === MediaModuleImageVariant.ONE_UP &&
                    styles.description1Up)
                }}
              />
            )}
            {emailCapture?.field?.label && (
              <EmailCaptureForm
                field={emailCapture?.field}
                id={id}
                submitCta={emailCapture.submitCta}
                variant={variant}
              />
            )}
            <DtvBox
              sx={{
                ...styles.ctaContainer,
                ...(variant === MediaModuleImageVariant.ONE_UP &&
                  styles.ctaContainer1Up)
              }}
            >
              {cta?.label && (
                <DtvButton
                  ariaLabel={cta?.ariaLabel || cta?.label}
                  id={`${id}-cta`}
                  role="button"
                  {...((({ ariaLabel, ...rest }) => rest)(cta || {}))}
                  googleAnalyticsData={getAnalyticsAttrs(
                    cta?.ariaLabel || cta?.label,
                    `media_module_image_card-primary-${
                      variant === MediaModuleImageVariant.ONE_UP ? "1up" : "2up"
                    }`,
                    handleGetTargeurl(cta?.targeturl)
                  )}
                  sx={{
                    ...styles.button,
                    ...styles.cta(cta?.color, cta?.backgroundColor),
                    ...(cta.size === "medium" && styles.buttonMedium),
                    ...(cta.size === "large" && styles.buttonLarge)
                  }}
                />
              )}
              {secondaryCta?.label && (
                <DtvButton
                  ariaLabel={secondaryCta?.ariaLabel || secondaryCta?.label}
                  id={`${id}-secondary-cta`}
                  {...((({ ariaLabel, ...rest }) => rest)(secondaryCta || {}))}
                  googleAnalyticsData={getAnalyticsAttrs(
                    secondaryCta?.ariaLabel || secondaryCta?.label,
                    `media_module_image_card-secondary-${
                      variant === MediaModuleImageVariant.ONE_UP ? "1up" : "2up"
                    }`,
                    handleGetTargeurl(secondaryCta?.targeturl)
                  )}
                  sx={{
                    ...styles.button,
                    ...styles.secondaryCta(
                      secondaryCta?.color,
                      secondaryCta?.backgroundColor
                    ),
                    ...(secondaryCta.size === "medium" && styles.buttonMedium),
                    ...(secondaryCta.size === "large" && styles.buttonLarge)
                  }}
                />
              )}
              {/* Generated by Copilot- Start - 2026-02-25 22:29:48 */}
              {tertiaryCta?.label && (
                <DtvButton
                  ariaLabel={tertiaryCta?.ariaLabel || tertiaryCta?.label}
                  id={`${id}-tertiary-cta`}
                  {...((({ ariaLabel, ...rest }) => rest)(tertiaryCta || {}))}
                  endIcon={
                    typeof tertiaryCta?.endIcon === "string" ? (
                      <DtvAvatar
                        alt={tertiaryCta?.imageAlt || ""}
                        src={tertiaryCta?.endIcon}
                        sx={{
                          height: "16px",
                          width: "16px"
                        }}
                      />
                    ) : null
                  }
                  googleAnalyticsData={getAnalyticsAttrs(
                    tertiaryCta?.ariaLabel || tertiaryCta?.label,
                    `media_module_image_card-tertiary-${
                      variant === MediaModuleImageVariant.ONE_UP ? "1up" : "2up"
                    }`,
                    handleGetTargeurl(tertiaryCta?.targeturl)
                  )}
                  sx={{
                    ...styles.button,
                    ...styles.tertiaryCta(
                      tertiaryCta?.color,
                      tertiaryCta?.backgroundColor
                    ),
                    ...(tertiaryCta.size === "medium" && styles.buttonMedium),
                    ...(tertiaryCta.size === "large" && styles.buttonLarge)
                  }}
                />
              )}
              {/* Generated by Copilot- End - 2026-02-25 22:29:48 */}
            </DtvBox>
            {legalText?.content && (
              <DtvText {...legalText} sx={styles.legalText} />
            )}
          </DtvBox>
          {isImageExists && (
            <DtvBox
              sx={{
                ...styles.imageMainContainer,
                ...(variant === MediaModuleImageVariant.ONE_UP &&
                  styles.imageMainContainer1Up)
              }}
            >
              <DtvBox
                sx={{
                  ...styles.imageContainer,
                  ...(variant === MediaModuleImageVariant.ONE_UP &&
                    styles.imageContainer1Up)
                }}
              >
                {aboveTheFold && (
                  <Head>
                    {image?.xl?.url && (
                      <link
                        as="image"
                        fetchPriority="high"
                        href={`${image?.xl?.url}`}
                        media={LDSK_SIZE}
                        rel="preload"
                      />
                    )}
                    {image?.lg?.url && (
                      <link
                        as="image"
                        fetchPriority="high"
                        href={`${image?.lg?.url}`}
                        media={DSK_SIZE}
                        rel="preload"
                      />
                    )}
                    {image?.md?.url && (
                      <link
                        as="image"
                        fetchPriority="high"
                        href={`${image?.md?.url}`}
                        media={LTP_SIZE}
                        rel="preload"
                      />
                    )}
                    {image?.sm?.url && (
                      <link
                        as="image"
                        fetchPriority="high"
                        href={`${image?.sm?.url}`}
                        media={TBL_SIZE}
                        rel="preload"
                      />
                    )}
                    {image?.xs?.url && (
                      <link
                        as="image"
                        fetchPriority="high"
                        href={`${image?.xs?.url}`}
                        media={MBL_SIZE}
                        rel="preload"
                      />
                    )}
                  </Head>
                )}
                <picture>
                  {/* Generated by Copilot- Start - 2026-04-10 17:28:57 */}
                  {image?.xl?.url && (
                    <source
                      media={LDSK_SIZE}
                      srcSet={`${image?.xl?.url}`}
                    />
                  )}
                  {image?.lg?.url && (
                    <source
                      media={DSK_SIZE}
                      srcSet={`${image?.lg?.url}`}
                    />
                  )}
                  {image?.md?.url && (
                    <source
                      media={LTP_SIZE}
                      srcSet={`${image?.md?.url}`}
                    />
                  )}
                  {image?.sm?.url && (
                    <source
                      media={TBL_SIZE}
                      srcSet={`${image?.sm?.url}`}
                    />
                  )}
                  {image?.xs?.url && (
                    <source
                      media={MBL_SIZE}
                      srcSet={`${image?.xs?.url}`}
                    />
                  )}
                  <img
                    alt={image?.alt || image?.md?.alt || image?.xl?.alt || image?.lg?.alt || image?.sm?.alt || image?.xs?.alt || ""}
                    loading={aboveTheFold ? "eager" : "lazy"}
                    src={`${(image?.md?.url || image?.sm?.url || image?.xs?.url)?.trim()}?imwidth=600`}
                    style={{
                      ...styles.image,
                      ...(variant === MediaModuleImageVariant.ONE_UP &&
                        styles.image1Up)
                    }}
                  />
                  {/* Generated by Copilot- End - 2026-04-10 17:28:57 */}
                </picture>
              </DtvBox>
            </DtvBox>
          )}
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    return (
      <ErrorDisplay component="Media Card Image 1-up 2-up V2" error={error} />
    );
  }
};
